/**
 * Central query-key factory (frontend-architecture R4).
 * Every hook in apps/ui/web reads keys from here — no hand-spelled array
 * literals at call sites. Keys follow the PLAN §5.3.2 hierarchy:
 * [domain, sub-domain, ...identifiers].
 */
export const keys = {
  workstream: {
    all: () => ['workstream'] as const,
    createChat: () => ['workstream', 'create', 'chat'] as const,
    create: () => ['workstream', 'create'] as const,
    /** Post-creation chat turn (Librarian), scoped to one workstream. */
    chat: (workstreamId: string) => ['workstream', 'chat', workstreamId] as const,
    /** The caller's workstreams (S-C1) — fed by `GET /api/v1/workstreams`. */
    listMine: () => ['workstream', 'list', 'mine'] as const,
    /** One workstream's persisted transcript (Drop 7 / S05, ADR-0047) —
     * `GET /workstreams/{id}/messages`. Seeds the client-held chat on reopen. */
    messages: (workstreamId: string) => ['workstream', 'messages', workstreamId] as const,
    /** One workstream's memory (ADR-0056) — `GET /workstreams/{id}/memory`. The
     * Memory rail's source; the write mutations invalidate it. */
    memory: (workstreamId: string) => ['workstream', 'memory', workstreamId] as const,
    /**
     * Memory write mutations (ADR-0056). Own `mutationKey`s so a failure is
     * attributable in the shared `MutationCache.onError` sink (which forwards
     * `mutation.options.mutationKey` to `reportError`) — consistent with every
     * other app mutation.
     */
    acceptMemory: (workstreamId: string) =>
      ['workstream', 'memory', 'accept', workstreamId] as const,
    deleteMemory: (workstreamId: string) =>
      ['workstream', 'memory', 'delete', workstreamId] as const,
    /** Every workstream's member agents — a retire (AB-2-5) removes it from all of them. */
    allMemberAgents: () => ['workstream', 'agents'] as const,
    /** One workstream's member agents (ADR-0080) — `GET /workstreams/{id}/agents`. */
    memberAgents: (workstreamId: string) => ['workstream', 'agents', workstreamId] as const,
    inviteAgent: (workstreamId: string) =>
      ['workstream', 'agents', 'invite', workstreamId] as const,
    removeAgent: (workstreamId: string) =>
      ['workstream', 'agents', 'remove', workstreamId] as const,
    /** One workstream's artifacts (Drop 6 / S09) — `GET /workstreams/{id}/artifacts`. */
    artifacts: (workstreamId: string) => ['workstream', 'artifacts', workstreamId] as const,
    /**
     * One artifact fetched for viewing (Drop 6 / S11) —
     * `GET /workstreams/{id}/artifacts/{artifactId}`. Nested UNDER the list key so
     * the S10 delete's list invalidation (prefix match) also drops a viewed file's
     * cache — a file deleted while open then 404s and the View pane self-heals.
     */
    artifact: (workstreamId: string, artifactId: string) =>
      ['workstream', 'artifacts', workstreamId, artifactId] as const,
    /**
     * Delete-artifact mutation (Drop 6 / S10). Its own `mutationKey` so a failed
     * delete is attributable in the shared `MutationCache.onError` sink (which
     * forwards `mutation.options.mutationKey` to `reportError`) — consistent with
     * every other app mutation.
     */
    deleteArtifact: (workstreamId: string) =>
      ['workstream', 'artifacts', 'delete', workstreamId] as const,
    /**
     * Delete-workstream mutation (delete-workstream flow). Its own `mutationKey`
     * so a failed delete is attributable in the shared `MutationCache.onError`
     * sink (which forwards `mutation.options.mutationKey` to `reportError`) —
     * consistent with every other app mutation.
     */
    deleteWorkstream: (workstreamId: string) => ['workstream', 'delete', workstreamId] as const,
    /** Rename-workstream mutation — attributable in the shared `MutationCache.onError` sink. */
    renameWorkstream: (workstreamId: string) => ['workstream', 'rename', workstreamId] as const,
    /**
     * Upload mutation (Drop 8 / Phase G). Its own `mutationKey` so a failed
     * upload is attributable in the shared `MutationCache.onError` sink (which
     * forwards `mutation.options.mutationKey` to `reportError`) — consistent
     * with every other app mutation.
     */
    uploadArtifact: (workstreamId: string) =>
      ['workstream', 'artifacts', 'upload', workstreamId] as const,
  },
  /** This-period usage (D13-05/07) — `GET /api/v1/workstreams/usage`. Your + org aggregate. */
  usage: {
    mine: () => ['usage', 'mine'] as const,
    /** The role-shaped report (D15-04) — `GET …/usage/report?period=`. `undefined` = current. */
    report: (period: string | undefined) => ['usage', 'report', period ?? 'current'] as const,
    /** The per-turn ledger (D16-03) — `GET …/usage/ledger?period=`. `undefined` = current. */
    ledger: (period: string | undefined) => ['usage', 'ledger', period ?? 'current'] as const,
  },
  /** The org activity feed (ADR-0076 §2, D16-04) — `GET /workstreams/activity`, Owner/Admin. */
  activity: {
    list: () => ['activity'] as const,
    /** Mutation key — a CSV export reported to the feed. */
    reportExport: () => ['activity', 'export'] as const,
  },
  /** The caller's platform permissions (ADR-0072) — `GET /api/v1/me`. */
  me: () => ['me'] as const,
  /** The tenant's plan + AUD figures (D15-09) — `GET /api/v1/workstreams/plan`, Owner only. */
  plan: () => ['plan'] as const,
  /** Budget settings + this period's alerts (ADR-0078, D17-01) — `GET/PUT /workstreams/budget`. */
  budget: {
    get: () => ['budget'] as const,
    /** Mutation key — attributable in the shared `MutationCache.onError` sink. */
    save: () => ['budget', 'save'] as const,
    /** Mutation key — the Owner raising this period's cap (D17-07). */
    extend: () => ['budget', 'extend'] as const,
  },
  /** The org's shared custom agents (ADR-0080) — `GET /workstreams/agents`. */
  orgAgents: () => ['agents', 'org'] as const,
  /** AB-2-2: the caller's own drafts — `GET /workstreams/agents/drafts` (the Agents rail). */
  drafts: () => ['agents', 'drafts'] as const,
  /** Mutation key — "New agent" (`POST /workstreams/builders`). */
  createBuilder: () => ['agents', 'builder', 'create'] as const,
  /** One builder's agent + draft revisions — `GET /workstreams/{id}/builder`. */
  builder: (workstreamId: string) => ['agents', 'builder', workstreamId] as const,
  /** Mutation key — publish / roll back the builder's agent (AB-2-4); one key, so the
   *  panel disables both controls while either is in flight. */
  liveVersion: (workstreamId: string) => ['agents', 'builder', 'live', workstreamId] as const,
  /** AB-2-5 — the org library and its share reviews (`GET /workstreams/agents/{library,reviews}`). */
  library: () => ['agents', 'library'] as const,
  reviews: () => ['agents', 'reviews'] as const,
  /** Mutation key — share / review / retire one agent; one key per agent so its row's
   *  buttons all disable while any of them is in flight. */
  libraryAction: (agentId: string) => ['agents', 'library', 'action', agentId] as const,
  /** Users & Access (ADR-0071 §6, D15-15) — `GET /workstreams/members` + its writes. */
  members: {
    list: () => ['members'] as const,
    /** Mutation keys — attributable in the shared `MutationCache.onError` sink. */
    invite: () => ['members', 'invite'] as const,
    changeRole: () => ['members', 'role'] as const,
    remove: () => ['members', 'remove'] as const,
    withdraw: () => ['members', 'withdraw'] as const,
  },
  /** The caller's tenant role (ADR-0071 §1, D15-01) — `GET /api/v1/workstreams/me`. */
  role: () => ['role'] as const,
  /** Admin eval runs (ADR-0072) — `GET /api/v1/admin/evals/runs[/{runId}]`. */
  evals: {
    runs: () => ['evals', 'runs'] as const,
    run: (runId: string) => ['evals', 'runs', runId] as const,
    /** Question sets + documents in the eval-fixtures bucket (ADR-0073). */
    sets: () => ['evals', 'sets'] as const,
    set: (name: string) => ['evals', 'sets', name] as const,
    documents: () => ['evals', 'documents'] as const,
    document: (name: string) => ['evals', 'documents', name] as const,
    /** Mutation keys — attributable in the shared `MutationCache.onError` sink. */
    saveSet: () => ['evals', 'sets', 'save'] as const,
    deleteSet: () => ['evals', 'sets', 'delete'] as const,
    saveDocument: () => ['evals', 'documents', 'save'] as const,
    deleteDocument: () => ['evals', 'documents', 'delete'] as const,
    uploadDocument: () => ['evals', 'documents', 'upload'] as const,
  },
} as const;
